import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import DateTimeFormatter from "../../../app/DateTimeFormatter";
import { ChartChatMessage, ChartData } from "../../chatModels";
import { getChartContainerId } from "./ChartRenderer";

export const downloadCSV = (data: ChartData, utcDateFormatter: DateTimeFormatter) => {
  try {
    // Format data for CSV
    const csvData = formatDataToCSV(data, utcDateFormatter);
    const fileName = `${data.title || "chart"}_${new Date().toISOString().split("T")[0]}.csv`;
    // Convert to CSV string
    const escapeColumn = (column: string) => `"${column.replaceAll('"', '""')}"`;
    const utf8Bom = "\ufeff";
    const csvContent = utf8Bom + csvData.map((row) => row.map(escapeColumn).join(",")).join("\n");
    // Download CSV file
    saveAs(new Blob([csvContent], { type: "text/csv" }), fileName);
  } catch (error) {
    console.error("Failed to download CSV:", error);
  }
};

export const formatDataToCSV = (
  chartData: ChartData,
  utcDateFormatter: DateTimeFormatter
): string[][] => {
  // Create headers
  const headers = ["Timestamp (UTC)"];
  if (chartData.series && chartData.series.length > 0) {
    for (const series of chartData.series) {
      const headerName = series.valueUnit ? `${series.key} (${series.valueUnit})` : series.key;
      headers.push(headerName);
    }
  } else {
    headers.push("Value");
  }

  // Create title row if title exists
  const titleRow = chartData.title
    ? [`${chartData.title} - Generated on ${new Date().toLocaleString()}`]
    : [];

  // Create data rows
  const dataRows = chartData.data.map((dataPoint) => {
    const date = new Date(dataPoint.timestamp);
    const utcTimestamp = utcDateFormatter.formatDateAndTime(date) + " UTC";
    return chartData.series.reduce(
      (accumulator, series) => {
        const value = dataPoint[series.key];
        if (typeof value === "number") {
          accumulator.push(value.toFixed(2)); // Format numbers to 2 decimal places
        } else {
          accumulator.push(value === undefined ? "" : value.toString());
        }
        return accumulator;
      },
      [utcTimestamp]
    );
  });

  // Combine all rows
  const csvData: string[][] = [];
  if (titleRow.length > 0) {
    csvData.push(titleRow, [""] /* Empty row for spacing */);
  }
  csvData.push(headers, ...dataRows);
  return csvData;
};

export const downloadChartImage = async (message: ChartChatMessage) => {
  try {
    const blob = await renderChartToBlob(message);
    // export the blob to a file download
    const fileName = `${message.content.title || "chart"}_${new Date().toISOString().split("T")[0]}.png`;
    saveAs(blob, fileName);
  } catch (error) {
    console.error("Failed to download chart image:", error);
  }
};

export const renderChartMessagesToDataUrls = async (
  messages: ChartChatMessage[]
): Promise<Record<string, string | null>> =>
  Object.fromEntries(
    await Promise.all(
      messages.map(async (msg) => {
        try {
          const blob = await renderChartToBlob(msg);
          const reader = new globalThis.FileReader();
          const dataUrl = await new Promise<string>((resolve, reject) => {
            reader.readAsDataURL(blob);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error("Failed to load image"));
          });
          return [msg.id, dataUrl];
        } catch (error) {
          console.error("Failed to render chart for clipboard:", error, msg);
          return [msg.id, null];
        }
      })
    )
  );

const renderChartToBlob = async (message: ChartChatMessage): Promise<Blob> => {
  const chartContainerId = getChartContainerId(message);
  const chartElRef = document.getElementById(chartContainerId);
  if (!chartElRef) {
    throw new Error("Chart element not found");
  }

  // wait for the DOM to settle
  await new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });
  // draw the chart DOM element to a canvas
  const canvas = await html2canvas(chartElRef, {
    backgroundColor: "#ffffff",
    scale: 2,
  });
  // create a blob from the canvas
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob: Blob | null) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("Failed to create image blob"));
      }
    });
  });
};
